import type { CompatibleMakeRef, CompatibleModelRef } from '@/lib/api/inventory.js';
import { listDeviceMakes, listDeviceModels } from '@/lib/api/devices.js';
import { createModelAlias, getCompatibilityCatalog } from '@/lib/api/device-suggest.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import {
  aliasCandidateFromName,
  suggestCompatibleDevices,
  type DeviceSuggestion,
} from '@fixflow/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

type Props = {
  partName: string;
  compatibleMakes: CompatibleMakeRef[];
  onCompatibleMakesChange: (v: CompatibleMakeRef[]) => void;
  compatibleModels: CompatibleModelRef[];
  onCompatibleModelsChange: (v: CompatibleModelRef[]) => void;
};

const CONFIDENCE_CLASS: Record<DeviceSuggestion['confidence'], string> = {
  high: 'text-emerald-700',
  medium: 'text-amber-700',
  low: 'text-slate-500',
};

export function CompatibleSection({
  partName,
  compatibleMakes,
  onCompatibleMakesChange,
  compatibleModels,
  onCompatibleModelsChange,
}: Props): ReactElement {
  const queryClient = useQueryClient();
  const [makePick, setMakePick] = useState('');
  const [modelPick, setModelPick] = useState('');
  const [debouncedName, setDebouncedName] = useState(partName);
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [aliasOffer, setAliasOffer] = useState<{
    alias: string;
    modelId: string;
    modelName: string;
  } | null>(null);
  const [aliasDismissed, setAliasDismissed] = useState<Set<string>>(() => new Set());
  const suggestedIds = useRef(new Set<string>());

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedName(partName), 300);
    return () => clearTimeout(timer);
  }, [partName]);

  const catalogQuery = useQuery({
    queryKey: ['device-compat-catalog'],
    queryFn: getCompatibilityCatalog,
    staleTime: 10 * 60 * 1000,
  });

  const suggestions = useMemo(() => {
    if (!catalogQuery.data) return [];
    return suggestCompatibleDevices(debouncedName, catalogQuery.data).filter(
      (suggestion) => !dismissed.has(suggestion.modelId),
    );
  }, [catalogQuery.data, debouncedName, dismissed]);

  useEffect(() => {
    const visibleIds = new Set(suggestions.map((suggestion) => suggestion.modelId));
    const next = compatibleModels.filter(
      (model) => !suggestedIds.current.has(model.modelId) || visibleIds.has(model.modelId),
    );
    let changed = next.length !== compatibleModels.length;
    for (const suggestion of suggestions) {
      if (suggestion.confidence !== 'high') continue;
      if (next.some((model) => model.modelId === suggestion.modelId)) continue;
      next.push({
        modelId: suggestion.modelId,
        modelName: suggestion.modelName,
        makeId: suggestion.makeId,
        makeName: suggestion.makeName,
      });
      suggestedIds.current.add(suggestion.modelId);
      changed = true;
    }
    for (const id of suggestedIds.current) {
      if (!next.some((model) => model.modelId === id)) suggestedIds.current.delete(id);
    }
    if (changed) onCompatibleModelsChange(next);
  }, [suggestions, compatibleModels, onCompatibleModelsChange]);

  const makesQuery = useQuery({ queryKey: ['device-makes'], queryFn: () => listDeviceMakes() });
  const modelsQuery = useQuery({
    queryKey: ['device-models', makePick],
    queryFn: () => listDeviceModels({ makeId: makePick }),
    enabled: Boolean(makePick),
  });

  function dismiss(modelId: string) {
    setDismissed((prev) => new Set(prev).add(modelId));
    onCompatibleModelsChange(compatibleModels.filter((model) => model.modelId !== modelId));
  }

  function toggleSuggestion(suggestion: DeviceSuggestion, checked: boolean) {
    if (!checked) {
      dismiss(suggestion.modelId);
      return;
    }
    setDismissed((prev) => {
      const next = new Set(prev);
      next.delete(suggestion.modelId);
      return next;
    });
    if (!compatibleModels.some((model) => model.modelId === suggestion.modelId)) {
      onCompatibleModelsChange([
        ...compatibleModels,
        {
          modelId: suggestion.modelId,
          modelName: suggestion.modelName,
          makeId: suggestion.makeId,
          makeName: suggestion.makeName,
        },
      ]);
    }
  }

  function addAllHigh() {
    const highs = suggestions.filter((suggestion) => suggestion.confidence === 'high');
    const missing = highs.filter(
      (suggestion) => !compatibleModels.some((model) => model.modelId === suggestion.modelId),
    );
    if (missing.length === 0) return;
    onCompatibleModelsChange([
      ...compatibleModels,
      ...missing.map((suggestion) => ({
        modelId: suggestion.modelId,
        modelName: suggestion.modelName,
        makeId: suggestion.makeId,
        makeName: suggestion.makeName,
      })),
    ]);
  }

  async function saveAlias() {
    if (!aliasOffer) return;
    try {
      await createModelAlias({ modelId: aliasOffer.modelId, alias: aliasOffer.alias });
      toastSuccess(`Saved “${aliasOffer.alias}” as an alias`);
      setAliasOffer(null);
      void queryClient.invalidateQueries({ queryKey: ['device-compat-catalog'] });
    } catch (error) {
      toastError(parseApiError(error).message);
    }
  }

  return (
    <section
      id="section-compatible"
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="mb-4 text-lg font-semibold text-slate-950">Compatible Devices</h2>

      {suggestions.length > 0 ? (
        <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Suggested from name
          </p>
          <ul className="mt-2 space-y-1">
            {suggestions.map((suggestion) => {
              const checked = compatibleModels.some((model) => model.modelId === suggestion.modelId);
              return (
                <li key={suggestion.modelId} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(event) => toggleSuggestion(suggestion, event.target.checked)}
                  />
                  <span className="flex-1">
                    {suggestion.makeName} {suggestion.modelName}
                  </span>
                  <span className={`text-xs font-semibold ${CONFIDENCE_CLASS[suggestion.confidence]}`}>
                    {suggestion.confidence}
                  </span>
                  <button
                    type="button"
                    className="text-xs text-slate-400 hover:text-red-600"
                    onClick={() => dismiss(suggestion.modelId)}
                  >
                    Dismiss
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            className="mt-2 text-xs font-semibold text-primary hover:underline"
            onClick={addAllHigh}
          >
            Add all high confidence
          </button>
          <p className="mt-1 text-xs text-slate-500">
            Checked devices are saved with the item. Uncheck any that do not fit.
          </p>
        </div>
      ) : null}

      {aliasOffer ? (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
          <p>
            Save “{aliasOffer.alias}” as an alias for {aliasOffer.modelName}? Future parts with this
            text will suggest it.
          </p>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              className="rounded-lg bg-amber-800 px-3 py-1.5 text-xs font-semibold text-white"
              onClick={() => void saveAlias()}
            >
              Save alias
            </button>
            <button
              type="button"
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-amber-900"
              onClick={() => {
                setAliasDismissed((prev) => new Set(prev).add(`${aliasOffer.modelId}:${aliasOffer.alias}`));
                setAliasOffer(null);
              }}
            >
              Not now
            </button>
          </div>
        </div>
      ) : null}

      <div className="mb-3 flex flex-wrap gap-2">
        {compatibleModels.map((model) => (
          <span
            key={model.modelId}
            className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-800"
          >
            {model.makeName ? `${model.makeName} ` : ''}
            {model.modelName}
            <button
              type="button"
              className="text-blue-500 hover:text-red-600"
              onClick={() => dismiss(model.modelId)}
            >
              ×
            </button>
          </span>
        ))}
        {compatibleMakes.map((make) => (
          <span
            key={make.makeId}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
          >
            {make.makeName} (make)
            <button
              type="button"
              className="hover:text-red-600"
              onClick={() =>
                onCompatibleMakesChange(compatibleMakes.filter((row) => row.makeId !== make.makeId))
              }
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={makePick}
          onChange={(event) => {
            setMakePick(event.target.value);
            setModelPick('');
          }}
        >
          <option value="">Select make…</option>
          {(makesQuery.data ?? []).map((make) => (
            <option key={make.id} value={make.id}>
              {make.name}
            </option>
          ))}
        </select>
        <select
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm"
          value={modelPick}
          onChange={(event) => setModelPick(event.target.value)}
          disabled={!makePick}
        >
          <option value="">Select model…</option>
          {(modelsQuery.data ?? []).map((model) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
          onClick={() => {
            const model = (modelsQuery.data ?? []).find((row) => row.id === modelPick);
            const make = (makesQuery.data ?? []).find((row) => row.id === makePick);
            if (model) {
              if (!compatibleModels.some((row) => row.modelId === model.id)) {
                onCompatibleModelsChange([
                  ...compatibleModels,
                  { modelId: model.id, modelName: model.name, makeId: model.makeId },
                ]);
              }
              const suggested = suggestions.some((row) => row.modelId === model.id);
              if (!suggested) {
                const candidate = aliasCandidateFromName(partName, model.name, suggestions);
                const key = candidate ? `${model.id}:${candidate}` : '';
                if (candidate && !aliasDismissed.has(key)) {
                  setAliasOffer({ alias: candidate, modelId: model.id, modelName: model.name });
                }
              }
            } else if (make && !compatibleMakes.some((row) => row.makeId === make.id)) {
              onCompatibleMakesChange([...compatibleMakes, { makeId: make.id, makeName: make.name }]);
            }
          }}
        >
          + Add device manually
        </button>
      </div>
    </section>
  );
}
